import { Component } from '@theme/component';
import { CartAddEvent, ThemeEvents } from '@theme/events';

const SEED_STORAGE_KEY = 'cart-drawer-recommendations-seed';
const recommendationCache = new Map();

/**
 * @typedef {Object} CartItem
 * @property {number|string} product_id
 * @property {number|string} variant_id
 * @property {string} key
 * @property {number} quantity
 * @property {string} [handle]
 * @property {string} [product_title]
 * @property {Record<string, string>} [properties]
 */

/**
 * @typedef {Object} RecommendationSeed
 * @property {string} productId
 * @property {string} variantId
 * @property {string} key
 */

/**
 * @param {CartItem} item
 * @returns {boolean}
 */
function isFeeLine(item) {
  const handle = item.handle?.toLowerCase();
  const title = item.product_title?.toLowerCase();

  return handle === 'engraving-charge' || title === 'engraving charge' || Boolean(item.properties?._engraving_parent_key);
}

/**
 * @param {CartItem[]} items
 * @returns {CartItem[]}
 */
function getEligibleItems(items) {
  return items.filter((item) => !isFeeLine(item));
}

/**
 * @param {CartItem} item
 * @returns {RecommendationSeed}
 */
function getSeed(item) {
  return {
    productId: item.product_id.toString(),
    variantId: item.variant_id.toString(),
    key: item.key,
  };
}

/**
 * @returns {RecommendationSeed | null}
 */
function readStoredSeed() {
  try {
    const value = sessionStorage.getItem(SEED_STORAGE_KEY);
    if (!value) return null;

    const seed = JSON.parse(value);
    if (!seed?.productId || !seed?.variantId || !seed?.key) return null;

    return seed;
  } catch (_) {
    return null;
  }
}

/**
 * @param {RecommendationSeed | null} seed
 */
function storeSeed(seed) {
  try {
    if (seed) {
      sessionStorage.setItem(SEED_STORAGE_KEY, JSON.stringify(seed));
    } else {
      sessionStorage.removeItem(SEED_STORAGE_KEY);
    }
  } catch (_) {
    // Storage can be unavailable in private browsing contexts.
  }
}

class CartDrawerRecommendations extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {string | null} */
  #currentCacheKey = null;

  /** @type {Map<string, number>} */
  #eligibleQuantities = new Map();

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#activeFetch?.abort();
  }

  #initialize() {
    if (this.dataset.enabled !== 'true') {
      this.#hide(false);
      return;
    }

    const eligibleItems = getEligibleItems(this.#getEmbeddedCartItems());
    this.#rememberEligibleQuantities(eligibleItems);

    if (eligibleItems.length === 0) {
      this.#hide();
      return;
    }

    const storedSeed = readStoredSeed();
    const storedItem = storedSeed ? eligibleItems.find((item) => item.key === storedSeed.key) : null;
    const seed = getSeed(storedItem ?? eligibleItems[0]);

    storeSeed(seed);
    this.#load(seed);
  }

  /**
   * @param {CartAddEvent|CustomEvent} event
   */
  #onCartUpdate = (event) => {
    if (this.dataset.enabled !== 'true') return;
    if (event.detail?.data?.didError) return;

    const cartItems = event.detail?.resource?.items;
    if (!Array.isArray(cartItems)) return;

    const eligibleItems = getEligibleItems(cartItems);
    const changedItem = eligibleItems.find(
      (item) =>
        !this.#eligibleQuantities.has(item.key) ||
        item.quantity > (this.#eligibleQuantities.get(item.key) ?? 0)
    );
    this.#rememberEligibleQuantities(eligibleItems);

    if (eligibleItems.length === 0) {
      this.#hide();
      return;
    }

    const storedSeed = readStoredSeed();

    if (event instanceof CartAddEvent) {
      const sourceVariantId = event.detail?.data?.variantId ?? event.detail?.sourceId;
      const sourceProductId = event.detail?.data?.productId;
      const addedItem =
        eligibleItems.find((item) => sourceVariantId && item.variant_id.toString() === sourceVariantId.toString()) ??
        eligibleItems.find((item) => sourceProductId && item.product_id.toString() === sourceProductId.toString()) ??
        changedItem;

      if (!addedItem) return;

      const seed = getSeed(addedItem);
      storeSeed(seed);
      this.#load(seed);
      return;
    }

    const storedItem = storedSeed ? eligibleItems.find((item) => item.key === storedSeed.key) : null;
    if (storedItem) return;

    const seed = getSeed(eligibleItems[0]);
    storeSeed(seed);
    this.#load(seed);
  };

  /**
   * @param {RecommendationSeed} seed
   */
  async #load(seed) {
    const { intent, limit, matchSeedVariant } = this.dataset;
    const cacheKey = [seed.productId, seed.variantId, intent, limit, matchSeedVariant].join(':');

    this.dataset.productId = seed.productId;

    if (this.#currentCacheKey === cacheKey && !this.hidden) return;

    const cachedRecommendation = recommendationCache.get(cacheKey);
    if (cachedRecommendation) {
      this.#applyRecommendation(cachedRecommendation);
      return;
    }

    this.#activeFetch?.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;
    this.setAttribute('aria-busy', 'true');

    try {
      const url = new URL(this.dataset.url, window.location.origin);
      url.searchParams.set('product_id', seed.productId);
      url.searchParams.set('variant', seed.variantId);
      url.searchParams.set('section_id', this.dataset.sectionId);
      url.searchParams.set('intent', intent);

      const response = await fetch(url, { signal: abortController.signal });
      if (!response.ok) throw new Error(`Server returned ${response.status}`);

      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const recommendations = documentFragment.querySelector('cart-drawer-recommendations');
      const content = recommendations?.querySelector('[data-recommendations-content]');
      const count = Number(recommendations?.querySelector('[data-recommendation-count]')?.dataset.recommendationCount);

      if (!content || !Number.isFinite(count)) {
        this.#hide(false);
        return;
      }

      const recommendation = { html: content.innerHTML, count, cacheKey };
      recommendationCache.set(cacheKey, recommendation);
      this.#applyRecommendation(recommendation);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;

      console.error('Cart drawer recommendations error:', error);
      this.#hide(false);
    } finally {
      if (this.#activeFetch === abortController) {
        this.removeAttribute('aria-busy');
        this.#activeFetch = null;
      }
    }
  }

  /**
   * @param {{html: string, count: number, cacheKey: string}} recommendation
   */
  #applyRecommendation(recommendation) {
    const content = this.querySelector('[data-recommendations-content]');
    if (!content) return;

    content.innerHTML = recommendation.html;
    this.#currentCacheKey = recommendation.cacheKey;
    this.dataset.recommendationsPerformed = 'true';
    this.hidden = recommendation.count === 0;
  }

  /**
   * @param {boolean} [clearSeed]
   */
  #hide(clearSeed = true) {
    this.hidden = true;
    this.#currentCacheKey = null;
    this.#activeFetch?.abort();

    if (clearSeed) storeSeed(null);
  }

  /**
   * @returns {CartItem[]}
   */
  #getEmbeddedCartItems() {
    const cartItems = this.querySelector('[data-cart-items]');
    if (!cartItems?.textContent) return [];

    try {
      const items = JSON.parse(cartItems.textContent);
      return Array.isArray(items) ? items : [];
    } catch (_) {
      return [];
    }
  }

  /**
   * @param {CartItem[]} items
   */
  #rememberEligibleQuantities(items) {
    this.#eligibleQuantities = new Map(items.map((item) => [item.key, item.quantity]));
  }
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}
